import { useState, useEffect, useCallback } from 'react';

/**
 * Hook complet pour la déconnexion automatique avec notification
 * @param {Object} options - Options de configuration
 * @param {number} options.timeout - Temps d'inactivité total (ms) - défaut: 15 minutes
 * @param {number} options.warningTime - Temps avant déconnexion pour afficher l'avertissement (ms) - défaut: 2 minutes
 * @param {boolean} options.enabled - Activer/désactiver - défaut: true
 * @param {boolean} options.showWarning - Afficher un avertissement - défaut: true
 * @returns {Object} - État et fonctions de contrôle
 */
export const useAutoLogout = (options = {}) => {
  const {
    timeout = 15 * 60 * 1000,      // 15 minutes
    warningTime = 2 * 60 * 1000,    // 2 minutes
    enabled = true,
    showWarning = true
  } = options;

  const [showWarningModal, setShowWarningModal] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(Math.floor(warningTime / 1000));

  // Fonction pour nettoyer les données d'authentification
  const clearAuthData = useCallback(() => {
    const keysToRemove = [
      'auth_token',
      'userDetails',
      'user',
      'token',
      'selectedPlan',
      'persist:root',
    ];
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
    sessionStorage.clear();
  }, []);

  // Fonction pour réinitialiser le timer
  const resetTimer = useCallback(() => {
    // Cacher l'avertissement s'il est visible
    setShowWarningModal(false);
    setTimeRemaining(Math.floor(warningTime / 1000));
    
    // Simuler une activité utilisateur
    const event = new Event('user-activity');
    window.dispatchEvent(event);
  }, [warningTime]);

  // Fonction pour déconnecter manuellement
  const logout = useCallback(() => {
    clearAuthData();
    window.location.reload();
  }, [clearAuthData]);

  useEffect(() => {
    if (!enabled) return;

    let timeoutId;
    let warningId;
    let countdownInterval;
    let events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    // Écouter l'activité utilisateur
    const handleUserActivity = () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (warningId) clearTimeout(warningId);
      if (countdownInterval) clearInterval(countdownInterval);
      
      // Cacher l'avertissement
      setShowWarningModal(false);
      setTimeRemaining(Math.floor(warningTime / 1000));
      
      // Timer pour l'avertissement
      if (showWarning) {
        warningId = setTimeout(() => {
          setShowWarningModal(true);
          
          // Compte à rebours
          countdownInterval = setInterval(() => {
            setTimeRemaining(prev => {
              if (prev <= 1) {
                clearInterval(countdownInterval);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }, timeout - warningTime);
      }
      
      // Timer pour la déconnexion
      timeoutId = setTimeout(() => {
        console.log('Inactivité détectée - Déconnexion automatique');
        clearAuthData();
        window.location.reload();
      }, timeout);
    };

    // Démarrer le timer initial
    handleUserActivity();

    // Ajouter les écouteurs d'événements
    events.forEach(event => {
      window.addEventListener(event, handleUserActivity, true);
    });

    // Écouter l'événement personnalisé de réinitialisation
    window.addEventListener('user-activity', handleUserActivity);

    // Vérification quand l'utilisateur revient sur la page
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const userDetails = localStorage.getItem('userDetails');
        
        if (userDetails) {
          try {
            const parsed = JSON.parse(userDetails);
            if (parsed.expireDate) {
              const expireDate = new Date(parsed.expireDate);
              const now = new Date();
              if (now > expireDate) {
                console.log('Token expiré - Déconnexion automatique');
                clearAuthData();
                window.location.reload();
                return;
              }
            }
          } catch (error) {
            console.error('Erreur lors de la vérification du token:', error);
            clearAuthData();
            window.location.reload();
            return;
          }
        }
      }
      handleUserActivity();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (warningId) clearTimeout(warningId);
      if (countdownInterval) clearInterval(countdownInterval);
      
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity, true);
      });
      
      window.removeEventListener('user-activity', handleUserActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [timeout, warningTime, enabled, showWarning, clearAuthData]);

  // Fonction pour formater le temps
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return {
    showWarningModal,
    timeRemaining,
    formatTime,
    resetTimer,
    logout,
    clearAuthData
  };
};
